/**
 * Миксин с общей логикой для страниц со списками дуэлей/туринров:
 * фильтры, запросы с query и тп
 */
import { mapState } from 'vuex'
import { PER_PAGE_DEFAULT } from '~/utils/constants'
import { getFiltersAndSort } from '~/utils/filters-sort'
import RangeInput from '~/components/RangeInput/RangeInput'
import BaseSelect from '~/components/BaseSelect/BaseSelect'
import PageHead from '~/components/PageHead/PageHead'
import Pagination from '~/components/Pagination/Pagination'
import MultiSelect from '~/components/MultiSelect/MultiSelect'

let componentFilters = {}
let componentSort = {}

const initialLimit = PER_PAGE_DEFAULT

function buildQueryFilters (route, $cookiz, $store) {
  const game = $store.state.games.gamesByName[route.params.game]
  const isDuels = route.name.startsWith('game-duels_')
  const isPhased = route.name.startsWith('game-phased-tournaments_')
  const isSponsored = !!route.query.sponsor

  let mode = 'tournaments'

  if (isPhased) {
    mode = 'phased-tournaments'
  }

  if (isDuels) {
    mode = 'duels'
  }

  const initial = getFiltersAndSort(route.params.game, mode)
  const defaultSort = initial.sort.value.split(':')
  let sort = { name: defaultSort[0], order: defaultSort[1] }
  let limit = $cookiz.get('per-page') || initialLimit
  let offset = 0

  if (route.query.limit) {
    limit = route.query.limit
  }

  if (route.query.page) {
    offset = route.query.page * limit
  }

  componentFilters = {
    ...Object.keys(initial.filters).reduce((result, key) => {
      result[key] = { ...initial.filters[key] }
      return result
    }, {})
  }

  const parsedFilters = Object.keys(initial.filters).reduce((result, key) => {
    const filter = { ...initial.filters[key] }

    if (route.query[key]) {
      if (route.query[key] === 'any') {
        filter.value = ''
        componentFilters[key].value = 'any'
      } else if (filter.type === 'range') {
        const parsedValue = route.query[key].split(':')

        filter.value = {
          from: parsedValue[0],
          to: parsedValue[1],
          activeCurrencies: parsedValue.slice(2).filter(curr => curr)
        }
        componentFilters[key].value = filter.value
      } else {
        filter.value = route.query[key]
        componentFilters[key].value = filter.value
      }
    }

    if (filter.value) {
      result[key] = filter
    }

    return result
  }, {})

  if (isSponsored) {
    parsedFilters.is_sponsored = {
      name: 'is_sponsored',
      options: [isSponsored.toString()],
      value: isSponsored.toString()
    }
  }

  if (route.query.sort && route.query.order) {
    sort = { name: route.query.sort, order: route.query.order }
  } else if (
    !isDuels &&
    ['cancelled', 'completed'].includes(parsedFilters?.state?.value)
  ) {
    sort.order = sort.order === 'asc' ? 'desc' : 'asc'
  } else if (isDuels && game?.hasDuelsDate) {
    sort.name = 'date'
    sort.order = 'asc'
  }

  componentSort = initial.sort
  componentSort.value = `${sort.name}:${sort.order}`
  componentSort.icon = sort.order === 'asc' ? 'arrow-up' : 'arrow-down'

  const filters = []
  const specialFilters = []

  Object.values(parsedFilters).forEach((filter) => {
    if (
      !filter.value ||
      (filter.type === 'range' && !Object.keys(filter.value).length)
    ) {
      return
    }

    let queryFilter

    if (filter.type === 'range') {
      if (filter.value.from || filter.value.to) {
        if (filter.name === 'rating') {
          if (filter.value.from) {
            filters.push({
              type: 'number',
              name: 'min_rating',
              values: [{ from: +filter.value.from }]
            })
          }

          if (filter.value.to) {
            filters.push({
              type: 'number',
              name: 'max_rating',
              values: [{ to: +filter.value.to }]
            })
          }
        } else {
          queryFilter = {
            name: filter.queryName || filter.name,
            type: 'number'
          }
          const values = [{}]

          if (filter.value.from) {
            values[0].from = filter.value.from

            if (filter.value.activeCurrencies?.[0] === 'money') {
              values[0].from = filter.value.from * 100
            }
          }

          if (filter.value.to) {
            values[0].to = filter.value.to

            if (filter.value.activeCurrencies?.[0] === 'money') {
              values[0].to = filter.value.to * 100
            }
          }

          queryFilter.values = values
        }
      }

      if (filter.value?.activeCurrencies?.length) {
        if (filter.isCurrencySpecial) {
          specialFilters.push({
            name: filter.currencyQueryName,
            type: 'string_list',
            values: filter.value.activeCurrencies.map(curr => ({
              text: curr
            }))
          })
        } else {
          filters.push({
            name: filter.currencyQueryName,
            type: 'string_list',
            values: filter.value.activeCurrencies.map(curr => ({
              text: curr
            }))
          })
        }

        if (!filter.value.from && !filter.value.to) {
          return
        }
      }
    } else if (filter.type === 'multi') {
      queryFilter = {
        name: filter.queryName || filter.name,
        type: 'string_list',
        values:
          filter.value
            .split(':')
            .map((val) => {
              // как-то попадают названия игр вместо id на бэк и валятся ошибки, может у кого-то сохранилось в закладках, тут преобразую имя в id
              if (filter.queryName === 'game_id') {
                if (!parseInt(val)) {
                  if ($store.state.games.gamesByName[val]) {
                    return { text: $store.state.games.gamesByName[val].id }
                  } else {
                    return null
                  }
                }
              }
              return { text: val }
            })
            .filter(val => !!val) || []
      }
    } else {
      queryFilter = {
        name: filter.queryName || filter.name,
        type: filter.type || 'string',
        values: [{ text: filter.value }]
      }
    }

    // показывать ready дуэли вместе с in_progress
    if (
      queryFilter?.name === 'state' &&
      queryFilter.values[0].text === 'in_progress'
    ) {
      queryFilter.type = 'string_list'
      queryFilter.values.push({ text: 'ready' })
    }

    // показывать template дуэли вместе с open
    if (
      isDuels &&
      queryFilter?.name === 'state' &&
      queryFilter.values[0].text === 'open'
    ) {
      queryFilter.type = 'string_list'
      queryFilter.values.push({ text: 'template' })
    }

    // показывать in_progress и moderation мега-турниры по фильтру «open»
    if (
      isPhased &&
      queryFilter?.name === 'state' &&
      queryFilter.values[0].text === 'open'
    ) {
      queryFilter.type = 'string_list'
      queryFilter.values.push({ text: 'in_progress' })
      queryFilter.values.push({ text: 'moderation' })
    }

    // показывать in_progress и moderation standoff-турниры по фильтру «open»
    if (
      game?.name === 'standoff' &&
      queryFilter?.name === 'state' &&
      queryFilter.values[0].text === 'open'
    ) {
      queryFilter.type = 'string_list'
      queryFilter.values.push({ text: 'moderation' })
    }

    if (queryFilter) {
      if (filter.isSpecial) {
        if (filter.isMultiple) {
          const separatedValues = filter.value.split(filter.valueSeparator)
          for (let i = 0; i < filter.fields.length; i++) {
            specialFilters.push({
              ...queryFilter,
              values: [
                {
                  text: separatedValues[i]
                }
              ],
              name: filter.fields[i]
            })
          }
        } else { specialFilters.push(queryFilter) }
      } else {
        filters.push(queryFilter)
      }
    }
  })

  return {
    offset,
    limit,
    sort,
    filters,
    special_filters: specialFilters
  }
}

export default {
  components: {
    PageHead,
    BaseSelect,
    RangeInput,
    Pagination,
    MultiSelect
  },

  scrollToTop: false,

  watchQuery: true,

  fetch ({ store, route }) {
    const isDuels = route.name.startsWith('game-duels_')
    const requests = []
    const game = route.params.game

    if (isDuels) {
      requests.push(store.dispatch('games/fetchRecommendedDuels', game))
    } else {
      requests.push(store.dispatch('games/fetchRecommendedTournaments', game))
    }

    return Promise.all(requests)
  },

  async asyncData ({ store, route, $cookiz, error, i18n }) {
    if (
      !route.name.startsWith('tournaments') &&
      !store.state.games.games.some(
        game => game.is_enabled && game?.name === route.params.game
      )
    ) {
      error({ statusCode: 404, message: i18n.t('gameWasNotFound') })
    }

    const actionByRoute = {
      'game-duels': 'games/fetchDuels',
      'game-tournaments': 'games/fetchTournaments',
      'game-phased-tournaments': 'games/fetchPhasedTournaments',
      tournaments: 'games/fetchTournaments'
    }
    const query = buildQueryFilters(route, $cookiz, store)
    const game = route.params.game
    let url

    let total = 0
    let items = []

    if (route.name.startsWith('tournaments')) {
      url = '/games/tournaments'
    }

    try {
      const data = await store.dispatch(
        actionByRoute[route.name.split('___')[0]],
        {
          game,
          query,
          url
        }
      )

      items = data.data
      total = data.meta?.count
    } catch (e) {
      console.error(`${new Date().toUTCString()} :: page list error ::`, e)
    }

    return {
      items,
      total,
      query,
      filters: {
        ...Object.keys(componentFilters).reduce((result, key) => {
          result[key] = {
            ...componentFilters[key],
            title: i18n.t(
              componentFilters[key].title || componentFilters[key].name
            )
          }
          return result
        }, {})
      },
      sort: {
        ...componentSort,
        title: i18n.t(componentSort.title || componentSort.name)
      },
      perPage: Number(query.limit),
      currentPage: Number(route.query.page || 0)
    }
  },

  data () {
    const savedView = this.$cookiz.get('list-view')

    return {
      listView: savedView || 'cells',
      isFiltersVisible: false
    }
  },

  computed: {
    ...mapState('games', [
      'gameRecommendedDuels',
      'gameRecommendedTournaments',
      'gamesByName'
    ]),
    ...mapState('games', {
      generalRecommendedTournaments: 'recommendedTournaments'
    }),

    game () {
      return this.gamesByName[this.$route.params.game]
    },

    listProps () {
      return { view: 'cells' }
    },

    fixedListProps () {
      return { view: 'list', isSmall: true }
    },

    list () {
      return this.items || []
    },

    pagesCount () {
      return Math.ceil(this.total / this.perPage)
    },

    recommendedDuels () {
      return this.gameRecommendedDuels[this.$route.params.game] || []
    },

    recommendedTournaments () {
      if (this.$route.name.startsWith('tournaments')) {
        return this.generalRecommendedTournaments
      }

      return this.gameRecommendedTournaments[this.$route.params.game] || []
    },

    isFiltered () {
      const isDuels = this.$route.name.startsWith('game-duels')
      const initial = getFiltersAndSort(
        this.$route.params.game,
        isDuels ? 'duels' : 'tournaments'
      )

      for (const key in this.filters) {
        if (this.filters[key].value !== initial.filters[key].value) {
          return true
        }
      }
      return false
    }
  },

  watch: {
    $route: {
      handler (current, prev) {
        if (process.server) {
          return
        }

        if (!prev) {
          this.$nuxt.$once('triggerScroll', () => {
            window.scrollTo(0, 0)
          })
        }
      },
      immediate: true
    }
  },

  methods: {
    async setQueryUrl (config = {}) {
      const isDuels = this.$route.name.startsWith('game-duels')

      const initial = getFiltersAndSort(
        this.$route.params.game,
        isDuels ? 'duels' : 'tournaments'
      )
      const query = {}

      if (this.perPage !== initialLimit) {
        query.limit = this.perPage
      }

      if (this.currentPage > 0) {
        query.page = this.currentPage
      }

      const shouldReverseSort =
        !isDuels &&
        !['cancelled', 'completed'].includes(this.filters.state.value) &&
        ['cancelled', 'completed'].includes(config.prevFilter?.value)
      let reversedSortValue = initial.sort.value.split(':')
      reversedSortValue[1] = reversedSortValue[1] === 'asc' ? 'desc' : 'asc'
      reversedSortValue = reversedSortValue.join(':')

      if (
        this.sort.value &&
        this.sort.value !== initial.sort.value &&
        !(shouldReverseSort && this.sort.value === reversedSortValue)
      ) {
        // eslint-disable-next-line prefer-const
        let [name, order] = this.sort.value.split(':')

        query.order = order
        query.sort = name
      }

      Object.keys(this.filters).forEach((key) => {
        const filter = this.filters[key]
        if (filter.value && filter.value !== initial.filters[key].value) {
          if (filter.type === 'range') {
            if (
              filter.value.from ||
              filter.value.to ||
              filter.value.activeCurrencies?.length
            ) {
              query[key] = [
                filter.value.from,
                filter.value.to,
                (filter.value.activeCurrencies || []).join(':')
              ].join(':')
            }
          } else {
            query[key] = filter.value
          }
        }
      })

      if (config.isSocketUpdate) {
        query.t = Date.now()
      }

      try {
        await this.$router.replace({ path: this.$route.path, query })
      } catch (err) {
        if (err.name === 'NavigationDuplicated') {
          console.warn('the same url')
        } else {
          throw err
        }
      }
    },

    onPageChange (page) {
      this.currentPage = page

      this.setQueryUrl()

      this.$nextTick(() => {
        this.$refs.content.scrollIntoView()
      })
    },

    onPerPageChange (perPage) {
      this.perPage = perPage
      this.currentPage = 0
      this.$cookiz.set('per-page', perPage)

      this.setQueryUrl()
    },

    toggleFilters () {
      this.isFiltersVisible = !this.isFiltersVisible
    },

    onFilterChange (filter, value) {
      const prevFilter = { ...this.filters[filter.name] }
      this.filters[filter.name].value = value
      this.currentPage = 0

      this.setQueryUrl({ prevFilter })
    },

    onSortChange (value) {
      this.sort.value = value
      this.sort.icon = value.includes(':asc') ? 'arrow-up' : 'arrow-down'
      this.currentPage = 0

      this.setQueryUrl()
    },

    setView (value) {
      this.listView = value
      this.$cookiz.set('list-view', value)
    },

    resetFilters () {
      const isDuels = this.$route.name.startsWith('game-duels')
      const initial = getFiltersAndSort(
        this.$route.params.game,
        isDuels ? 'duels' : 'tournaments'
      )

      for (const key in this.filters) {
        this.filters[key].value = initial.filters[key].value
      }

      this.setQueryUrl()
    }
  }
}
